/**
 * CloudStarterRepository — Cloud-first repository.
 *
 * All reads and writes go directly to the API.
 * No local IndexedDB storage — the cloud is the single source of truth.
 */

import type { StarterRepository } from './StarterRepository';
import type { StarterData, FeedingEntry } from '@/types/starter';

interface CloudStarterPayload {
  starter: {
    id: string;
    createdDate: string;
    lastFedDate: string;
    totalFeedings: number;
    isActive: boolean;
    name?: string;
  } | null;
  recentFeedings: Array<{
    id: string;
    date: string;
    notes: string | null;
    source: string;
    createdAt: string;
  }>;
  lastUpdated: string | null;
}

export class CloudStarterRepository implements StarterRepository {
  private apiBaseUrl: string;
  private getToken: () => string | null;

  constructor(apiBaseUrl: string, getToken: () => string | null) {
    this.apiBaseUrl = apiBaseUrl;
    this.getToken = getToken;
  }

  private async fetchApi<T>(endpoint: string, options: { method?: string; body?: unknown } = {}): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['X-Session-Token'] = token;

    const response = await fetch(`${this.apiBaseUrl}${endpoint}`, {
      method: options.method ?? 'GET',
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    if (!response.ok) {
      const msg = await response.text().catch(() => '');
      throw new Error(`API error ${response.status}: ${msg}`);
    }

    return response.json() as Promise<T>;
  }

  async getStarter(): Promise<StarterData | undefined> {
    const data = await this.fetchApi<CloudStarterPayload>('/starter');
    if (!data.starter) return undefined;
    return this.mapStarter(data.starter);
  }

  async createStarter(createdDate: string, name?: string): Promise<StarterData> {
    const payload = {
      createdDate,
      lastFedDate: createdDate,
      name: name ?? 'My Starter',
      isActive: true,
    };
    await this.fetchApi('/starter/status', { method: 'PUT', body: payload });
    const data = await this.fetchApi<CloudStarterPayload>('/starter');
    if (!data.starter) throw new Error('Failed to create starter');
    return this.mapStarter(data.starter);
  }

  async updateStarter(updates: Partial<StarterData>): Promise<StarterData> {
    await this.fetchApi('/starter/status', { method: 'PUT', body: updates });
    const data = await this.fetchApi<CloudStarterPayload>('/starter');
    if (!data.starter) throw new Error('Starter not found');
    return this.mapStarter(data.starter);
  }

  async recordFeeding(date: string, notes?: string): Promise<StarterData> {
    await this.fetchApi('/starter/feeding', {
      method: 'POST',
      body: { date, notes, source: 'pwa' },
    });
    const data = await this.fetchApi<CloudStarterPayload>('/starter');
    if (!data.starter) throw new Error('Starter not found');
    return this.mapStarter(data.starter);
  }

  async getFeedingHistory(limit?: number): Promise<FeedingEntry[]> {
    const data = await this.fetchApi<CloudStarterPayload>('/starter');
    const feedings = data.recentFeedings.map((f) => this.mapFeeding(f));
    feedings.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return limit ? feedings.slice(0, limit) : feedings;
  }

  async deleteFeeding(id: string): Promise<void> {
    await this.fetchApi(`/starter/feeding/${id}`, { method: 'DELETE' });
  }

  private mapStarter(s: NonNullable<CloudStarterPayload['starter']>): StarterData {
    return {
      id: 'main',
      createdDate: s.createdDate,
      lastFedDate: s.lastFedDate,
      totalFeedings: s.totalFeedings,
      name: s.name ?? 'My Starter',
      syncStatus: 'synced',
      lastSyncedAt: new Date().toISOString(),
    };
  }

  private mapFeeding(f: CloudStarterPayload['recentFeedings'][0]): FeedingEntry {
    return {
      id: f.id,
      starterId: 'main',
      date: f.date,
      notes: f.notes ?? undefined,
      source: (f.source as FeedingEntry['source']) ?? 'app',
      syncStatus: 'synced',
      createdAt: f.createdAt,
    };
  }
}
